import { supabase } from '../lib/supabase';

class AuthService {
  // Sign in with email and password
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Update last login timestamp
      if (data?.user) {
        try {
          await supabase?.rpc('update_last_login');
        } catch (loginError) {
          console.error('Error updating last login:', loginError);
        }
      }
      
      return { user: data?.user, session: data?.session };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        throw new Error('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.');
      }
      throw error;
    }
  }

  // Sign up with email and password
  async signUp(email, password, metadata = {}) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return { user: data?.user, session: data?.session };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        throw new Error('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.');
      }
      throw error;
    }
  }

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Get current session
  async getSession() {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      
      if (error) {
        throw new Error(error.message);
      }
      
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      throw error;
    }
  }

  // Get current user
  async getUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      
      if (error) {
        throw new Error(error.message);
      }
      
      return user;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  // Get user profile from profiles table
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('profiles')?.select('*')?.eq('id', userId)?.single();
      if (error) {
        throw new Error(error.message);
      }
      return data;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  // Check if user is admin
  async checkAdminStatus(user) {
    if (!user) return false;
    
    try {
      // Check auth metadata first
      const authMetaRole = user?.user_metadata?.role || user?.raw_user_meta_data?.role;
      const appMetaRole = user?.app_metadata?.role || user?.raw_app_meta_data?.role;
      
      if (authMetaRole === 'admin' || appMetaRole === 'admin') {
        return true;
      }
      
      // Check profile role
      const profile = await this.getUserProfile(user?.id);
      return profile?.role === 'admin';
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Reset password
  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return true;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  // Update password
  async updatePassword(newPassword) {
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return true;
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }

  // Update user metadata
  async updateUserMetadata(metadata) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        data: metadata
      });
      if (error) {
        throw new Error(error.message);
      }
      return data?.user;
    } catch (error) {
      console.error('Error updating user metadata:', error);
      throw error;
    }
  }
}

export const authService = new AuthService();